"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { TIERS, type Tier } from "@engine/types";

interface Props {
  puzzleId: string;
  signedIn: boolean;
  actions: {
    rate: (input: unknown) => Promise<{ saved: boolean }>;
    report: (input: unknown) => Promise<{ filed: boolean; reason?: string }>;
  };
  /** The board as the player left it, so a report can be reproduced. */
  snapshot: Record<string, unknown>;
}

/**
 * Rating and reporting, shown once a case is solved.
 *
 * The difficulty question is the valuable one: comparing what players felt against what the grader
 * computed is how we find cases whose tier is wrong.
 */
export function AfterGame({ puzzleId, signedIn, actions, snapshot }: Props) {
  const t = useTranslations("feedback");
  const tiers = useTranslations("tiers");

  const [rating, setRating] = useState(0);
  const [perceived, setPerceived] = useState<Tier | "">("");
  const [comment, setComment] = useState("");
  const [rated, setRated] = useState(false);

  const [reporting, setReporting] = useState(false);
  const [category, setCategory] = useState("wrong_clue");
  const [reportText, setReportText] = useState("");
  const [reportState, setReportState] = useState<string | null>(null);

  if (!signedIn) {
    return (
      <p data-testid="feedback-signed-out" style={panelStyle}>
        {t("signInToRate")}
      </p>
    );
  }

  const submitRating = async (): Promise<void> => {
    if (rating === 0) return;
    const result = await actions.rate({
      puzzleId,
      rating,
      ...(perceived === "" ? {} : { perceivedTier: perceived }),
      ...(comment.trim() === "" ? {} : { comment: comment.trim() }),
    });
    if (result.saved) setRated(true);
  };

  const submitReport = async (): Promise<void> => {
    const result = await actions.report({
      puzzleId,
      category,
      message: reportText.trim(),
      boardSnapshot: snapshot,
    });
    setReportState(result.filed ? "filed" : (result.reason ?? "invalid"));
    if (result.filed) setReportText("");
  };

  return (
    <section style={panelStyle} data-testid="after-game">
      <h2 style={{ margin: "0 0 0.5rem", fontSize: "1.1rem" }}>{t("rateTitle")}</h2>

      {rated ? (
        <p data-testid="rating-thanks" style={{ margin: 0 }}>
          {t("thanks")}
        </p>
      ) : (
        <div style={{ display: "grid", gap: "0.75rem" }}>
          <div role="group" aria-label={t("rateTitle")} style={{ display: "flex", gap: "0.3rem" }}>
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                data-testid={`rate-${value}`}
                aria-pressed={rating === value}
                onClick={() => setRating(value)}
                style={{ ...smallButton, ...(rating >= value ? activeButton : {}) }}
              >
                ★
              </button>
            ))}
          </div>

          <label style={{ display: "grid", gap: "0.3rem" }}>
            {t("howHard")}
            <select
              data-testid="perceived-tier"
              value={perceived}
              onChange={(event) => setPerceived(event.target.value as Tier | "")}
              style={inputStyle}
            >
              <option value="">{t("noOpinion")}</option>
              {TIERS.map((tier) => (
                <option key={tier} value={tier}>
                  {tiers(tier)}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: "grid", gap: "0.3rem" }}>
            {t("comment")}
            <textarea
              data-testid="rating-comment"
              value={comment}
              maxLength={500}
              rows={2}
              onChange={(event) => setComment(event.target.value)}
              style={inputStyle}
            />
          </label>

          <button
            type="button"
            data-testid="submit-rating"
            disabled={rating === 0}
            onClick={() => void submitRating()}
            style={{ ...smallButton, justifySelf: "start" }}
          >
            {t("send")}
          </button>
        </div>
      )}

      <hr style={{ margin: "1.25rem 0", border: 0, borderTop: "1px solid rgba(128,128,128,0.25)" }} />

      {!reporting ? (
        <button type="button" data-testid="open-report" onClick={() => setReporting(true)} style={smallButton}>
          {t("reportProblem")}
        </button>
      ) : (
        <div style={{ display: "grid", gap: "0.6rem" }}>
          <label style={{ display: "grid", gap: "0.3rem" }}>
            {t("whatIsWrong")}
            <select
              data-testid="report-category"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              style={inputStyle}
            >
              <option value="wrong_clue">{t("categoryWrongClue")}</option>
              <option value="unsolvable">{t("categoryUnsolvable")}</option>
              <option value="display">{t("categoryDisplay")}</option>
              <option value="other">{t("categoryOther")}</option>
            </select>
          </label>

          <textarea
            data-testid="report-message"
            value={reportText}
            maxLength={1000}
            rows={3}
            placeholder={t("reportPlaceholder")}
            onChange={(event) => setReportText(event.target.value)}
            style={inputStyle}
          />

          <button
            type="button"
            data-testid="submit-report"
            disabled={reportText.trim().length === 0}
            onClick={() => void submitReport()}
            style={{ ...smallButton, justifySelf: "start" }}
          >
            {t("send")}
          </button>

          {reportState && (
            <p data-testid="report-state" style={{ margin: 0 }}>
              {reportState === "filed"
                ? t("reportThanks")
                : reportState === "rate_limited"
                  ? t("reportTooMany")
                  : t("reportFailed")}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

const panelStyle = {
  padding: "1rem",
  border: "1px solid rgba(128,128,128,0.35)",
  borderRadius: "0.5rem",
} as const;

const smallButton = {
  padding: "0.5rem 0.8rem",
  fontSize: "0.95rem",
  borderRadius: "0.4rem",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "rgba(128,128,128,0.45)",
  background: "transparent",
  color: "inherit",
  cursor: "pointer",
  minHeight: "2.5rem",
} as const;

const activeButton = { background: "var(--accent)", borderColor: "var(--accent)", color: "#fff" } as const;

const inputStyle = {
  padding: "0.5rem",
  fontSize: "1rem",
  borderRadius: "0.4rem",
  border: "1px solid rgba(128,128,128,0.45)",
  background: "transparent",
  color: "inherit",
} as const;
